<script lang="ts">
  import { get, writable } from "svelte/store";
  import { onMount } from "svelte";
  import { page } from "$app/state";
  import { goto } from "$app/navigation";
  import { authStore } from "$lib/stores";
  import { dotsHorizontalIcon } from "$lib/images/icons";
  import { searchHistoryStore, groupSearches, SearchHistoryManager } from "$lib/stores/searchHistoryStore";
  import { SVG } from "$lib/components";
  import type { SearchMetadata, GroupedSearches } from "$lib/types";
  import { Trash2 } from "lucide-svelte";
  import { DropdownMenu } from "$lib/components/ui";
  import { SearchAPI } from "$lib/api/search";
  import { User, Building2, Dot, BadgeCheck } from "lucide-svelte";
  import { cn } from "$lib/utility";
  import { routes } from "$lib/routes";
  import { Portal } from "bits-ui";

  type $$Events = {
    searchHistoryNavigation: CustomEvent<any>;
  };

  // TODO: Replace with standard event dispatcher
  import { createEventDispatcher } from "svelte";
  const dispatch = createEventDispatcher();

  let containerRef = $state<HTMLDivElement>();
  let observerTarget = $state<HTMLDivElement>();
  let observer = $state<IntersectionObserver>();

  let isUsingLocalData = $state(true);

  const BATCH_SIZE = 30;
  const FETCH_DELAY = 500;
  const PLACEHOLDER_SEARCHES = 5;

  let isLoading = $state(true);
  let isLoadingMore = $state(false);
  let hasMoreResults = $state(true);
  let currentOffset = $state(0);
  let error = $state<string | null>(null);
  let lastFetchTime = $state(0);
  let isInitialLoad = $state(true);
  let initialLoadComplete = $state(false);
  let previousUserId = $state<string | null>(null);

  const searchHistoryState = writable({
    isInitialized: false,
    lastKnownOffset: 0,
    hasMore: true,
  });

  let isMounted = $state(false);

  let lastSyncTime = $state<number>(0);
  let isSyncing = $state(false);

  const groupedSearchHistory = $derived(() => {
    const userID = SearchHistoryManager.getUserIdentifier($authStore.user);
    const relevantSearches = $searchHistoryStore.filter(
      (search) =>
        search.userID === userID || (search.userID === SearchHistoryManager.ANONYMOUS_USER && !$authStore.user),
    );
    return groupSearches(relevantSearches);
  });

  async function loadSearchHistory(offset: number = 0): Promise<void> {
    const userId = get(authStore).user?.id;
    if (!userId) return;

    const localSearches = searchHistoryStore.getSearchesForCurrentUser();

    isLoading = true;
    searchHistoryStore.setSearches([]);

    if (localSearches.length > 0) {
      isUsingLocalData = true;
      await new Promise((resolve) => setTimeout(resolve, 100));

      const initialChunk = localSearches.slice(0, BATCH_SIZE);
      searchHistoryStore.setSearches(initialChunk);
      currentOffset = initialChunk.length;
      hasMoreResults = true;

      isInitialLoad = true;
      initialLoadComplete = false;
      isLoading = false;

      checkViewportFill();
    } else {
      isUsingLocalData = false;
      await fetchSearchHistory(offset);
      isLoading = false;
      checkViewportFill();
    }

    searchHistoryState.set({
      isInitialized: true,
      lastKnownOffset: currentOffset,
      hasMore: hasMoreResults,
    });
  }

  async function syncWithServer(): Promise<void> {
    const userId = get(authStore).user?.id;
    if (!userId || isSyncing) return;

    try {
      isSyncing = true;
      const serverSearches = await SearchAPI.getPaginatedSearchesForUser(Number(userId), BATCH_SIZE, 0);

      if (serverSearches && Array.isArray(serverSearches)) {
        const localSearches = searchHistoryStore.getSearchesForCurrentUser();
        const hasChanges = compareSearchHistories(localSearches, serverSearches);

        if (hasChanges) {
          isLoadingMore = true;
          await Promise.resolve();
          searchHistoryStore.setSearches(serverSearches);
          await Promise.resolve();

          currentOffset = serverSearches.length;
          hasMoreResults = serverSearches.length >= BATCH_SIZE;

          // Update persistent state
          searchHistoryState.set({
            isInitialized: true,
            lastKnownOffset: currentOffset,
            hasMore: hasMoreResults,
          });
        }
      }

      lastSyncTime = Date.now();
    } catch (err) {
      console.error("Error syncing with server:", err);
    } finally {
      isSyncing = false;
      isLoadingMore = false;
    }
  }

  // Helper to compare local and server data
  function compareSearchHistories(local: any[], server: any[]): boolean {
    if (local.length !== server.length) return true;

    // Compare latest items' slugs and update timestamps
    const latestLocal = local[0];
    const latestServer = server[0];

    if (!latestLocal || !latestServer) return true;

    return (
      latestLocal.slug !== latestServer.slug ||
      new Date(latestLocal.updatedAt).getTime() !== new Date(latestServer.updatedAt).getTime()
    );
  }

  $effect(() => {
    if ($authStore.user?.id && previousUserId !== $authStore.user?.id) {
      // logger.log("Effect: authStore.user.id changed", $authStore.user.id);
      hasMoreResults = true;
      previousUserId = $authStore.user.id;
      loadSearchHistory(currentOffset);
    }
  });

  $effect(() => {
    const userId = get(authStore).user?.id ?? null;
    const state = get(searchHistoryState);

    if (userId !== previousUserId || !state.isInitialized) {
      previousUserId = userId;

      if (userId) {
        // Always start fresh on user change
        currentOffset = 0;
        hasMoreResults = true;
        isLoading = true;
        isInitialLoad = true;
        initialLoadComplete = false;
        searchHistoryStore.setSearches([]); // Clear current view

        loadSearchHistory(0).then(() => {
          if (hasMoreResults) {
            checkViewportFill();
          }
          setupObserver();
        });
      } else {
        isLoading = false;
        hasMoreResults = false;
        currentOffset = 0;
        searchHistoryStore.clearHistory();
      }
    }
  });

  function setupObserver() {
    if (observer) {
      observer.disconnect();
    }

    observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMoreResults && !isLoadingMore) {
          // logger.log("Intersection observer triggered"); // Debug log
          fetchSearchHistory(currentOffset);
        }
      },
      {
        root: null,
        rootMargin: "100px",
        threshold: 0,
      },
    );

    if (observerTarget) {
      observer.observe(observerTarget);
    }
  }

  // Modify delete handler to update local store immediately
  async function handleDelete(slug: string, query: string) {
    try {
      // Update local store immediately
      searchHistoryStore.removeSearch(slug);

      if (page.params.slug === slug) {
        goto(routes.searches);
      }

      // Then sync with server
      await SearchAPI.removeSearchBySlug(slug);
      // logger.log(`Deleted search: ${query}`);
    } catch (err) {
      console.error("Error deleting search:", err);
      // On error, sync with server to ensure consistency
      syncWithServer();
    }
  }

  // Modify checkViewportFill to handle the transition
  async function checkViewportFill() {
    if (!containerRef || !observerTarget || !hasMoreResults || isLoadingMore || !isMounted) return;

    const containerHeight = containerRef.clientHeight;
    const scrollHeight = containerRef.scrollHeight;
    const threshold = containerHeight * 1.2;

    if (scrollHeight < threshold && hasMoreResults) {
      const localSearches = searchHistoryStore.getSearchesForCurrentUser();

      if (currentOffset < localSearches.length && isUsingLocalData) {
        // Handle local data
        isLoadingMore = true;
        await new Promise((resolve) => setTimeout(resolve, 100));

        const originalSearches = localSearches.slice(0, currentOffset);
        const nextChunkEnd = Math.min(currentOffset + BATCH_SIZE, localSearches.length);
        const nextChunk = localSearches.slice(currentOffset, nextChunkEnd);

        searchHistoryStore.setSearches([...originalSearches, ...nextChunk]);
        currentOffset = nextChunkEnd;

        // Check if we've exhausted local data
        if (currentOffset >= localSearches.length) {
          isUsingLocalData = false;
          hasMoreResults = true; // Reset to allow server fetching
        } else {
          hasMoreResults = true;
        }

        isLoadingMore = false;

        if (scrollHeight < threshold && hasMoreResults && isUsingLocalData) {
          setTimeout(() => checkViewportFill(), 100);
        }
      } else {
        // Switch to server data
        isUsingLocalData = false;
        setupObserver();
      }
    } else {
      setupObserver();
    }

    isInitialLoad = false;
    initialLoadComplete = true;
  }

  async function fetchSearchHistory(offset: number = 0): Promise<void> {
    // logger.log("Fetching history with offset /1:", get(authStore).user?.id, !hasMoreResults, isLoadingMore);

    if (!get(authStore).user?.id || !hasMoreResults || isLoadingMore) return;

    // logger.log("Fetching history with offset /2:", offset);

    const now = Date.now();
    if (now - lastFetchTime < FETCH_DELAY && !isInitialLoad) return;

    // logger.log("Fetching history with offset /3:", offset);

    try {
      isLoadingMore = true;
      lastFetchTime = now;

      const searches = await SearchAPI.getPaginatedSearchesForUser(Number(get(authStore).user?.id), BATCH_SIZE, offset);

      if (searches && Array.isArray(searches)) {
        hasMoreResults = searches.length >= BATCH_SIZE;

        if (offset === 0) {
          searchHistoryStore.setSearches(searches);
        } else {
          searchHistoryStore.appendSearches(searches);
        }

        currentOffset = offset + searches.length;
        // logger.log("Updated offset:", currentOffset, "hasMore:", hasMoreResults);
      }

      error = null;
    } catch (err) {
      console.error("Error fetching search history:", err);
      error = err instanceof Error ? err.message : "Failed to load search history";
      hasMoreResults = false;
    } finally {
      isLoading = false;
      isLoadingMore = false;
    }
  }

  function navigateToSearch(slug: string) {
    goto(`/search/${slug}`);
  }

  function handleResize() {
    if (isInitialLoad) {
      checkViewportFill();
    }
  }

  onMount(() => {
    isMounted = true;
    // logger.log("Component mounted");
    // logger.log("Current user:", get(authStore).user);

    if (get(authStore).isLoggedIn) {
      window.addEventListener("resize", handleResize);
    } else {
      isLoading = false;
    }

    return () => {
      isMounted = false;
      if (observer) {
        observer.disconnect();
      }
      window.removeEventListener("resize", handleResize);

      // Save state before unmount
      searchHistoryState.set({
        isInitialized: true,
        lastKnownOffset: currentOffset,
        hasMore: hasMoreResults,
      });
    };
  });
</script>

<div bind:this={containerRef} class="custom-scrollbar flex-1 overflow-y-auto overflow-x-hidden">
  <div class="mt-1.5 flex flex-col gap-5 text-xs">
    {#each Object.entries(groupedSearchHistory()) as [group, searches]}
      {@const visibleSearches = searches.filter((search) => search.status !== "invisible")}
      {#if visibleSearches.length > 0}
        <div>
          <div class="mb-2 font-bold dark:text-theme-gray-700">{group}</div>
          <ul>
            {#each visibleSearches as search (search.slug)}
              <li class="mb-3 flex items-center justify-between gap-1 rounded-base font-light">
                <button
                  class={cn(
                    "flex-grow truncate text-left lg:max-w-[200px]",
                    "text-theme-text-primary dark:text-theme-gray-600", // Muted text
                    "hover:font-bold dark:hover:text-theme-gray-800", // Brighter on hover
                    "mr-2 transition-all",
                    page.params.slug === search.slug ? "font-bold dark:text-theme-gray-800" : "transition-default",
                  )}
                  onclick={(event) => {
                    dispatch("searchHistoryNavigation", {});
                    navigateToSearch(search.slug);
                  }}
                >
                  {#if search.mode === "pro"}
                    <BadgeCheck
                      strokeWidth={1.5}
                      size={14}
                      class="mb-[1px] inline fill-black text-white dark:fill-theme-green dark:text-black"
                    />
                  {/if}
                  {search.query}
                </button>

                <div class="group/search-item flex items-center">
                  <span class="mr-2 flex items-center justify-between gap-2">
                    <span class="text-theme-text-primary dark:text-theme-gray-500">
                      {search.matchesCount ?? 0}
                    </span>
                    {#if search.category === "person"}
                      <User class="inline h-3.5 w-3.5 dark:text-theme-gray-500" strokeWidth={1.5} />
                    {:else if search.category === "company"}
                      <Building2 class="inline h-3.5 w-3.5 dark:text-theme-gray-500" strokeWidth={1} />
                    {:else}
                      <Dot class="inline h-3.5 w-3.5 dark:text-theme-gray-500" strokeWidth={1} />
                    {/if}
                  </span>

                  <!-- Three dots menu -->
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger>
                      <button
                        class="opacity-0 transition-opacity duration-200 group-hover/search-item:opacity-100 dark:text-theme-gray-500 dark:hover:text-theme-gray-800"
                      >
                        <SVG className="text-theme-text-primary dark:text-theme-gray-500" file={dotsHorizontalIcon} />
                      </button>
                    </DropdownMenu.Trigger>
                    <Portal>
                      <DropdownMenu.Content
                        side="top"
                        class="!rounded-base !p-0 shadow-sm dark:border dark:border-white/10 dark:bg-theme-background-tertiary"
                      >
                        <DropdownMenu.Item
                          class={cn(
                            "hover:cursor-pointer",
                            "text-theme-red dark:text-theme-gray-600 dark:hover:text-theme-red",
                            "flex items-center gap-2 text-xs",
                            "dark:hover:bg-theme-background-primary",
                          )}
                          onclick={() => handleDelete(search.slug, search.query)}
                        >
                          <Trash2 class="h-4 w-4" strokeWidth={1} />
                          <span>Delete</span>
                        </DropdownMenu.Item>
                      </DropdownMenu.Content>
                    </Portal>
                  </DropdownMenu.Root>
                </div>
              </li>
            {/each}
          </ul>
        </div>
      {/if}
    {/each}

    {#if Object.entries(groupedSearchHistory()).length === 0 && !isLoading && !isLoadingMore}
      <div class="text-theme-text-secondary dark:text-theme-gray-500">Your searches will appear here</div>
    {/if}

    <div class="invisible w-full">
      <div bind:this={observerTarget} class="h-0 w-full" aria-hidden="true"></div>
    </div>

    <!-- Intersection Observer Target -->
    <!-- <div bind:this={observerTarget} class="h-px w-full"></div> -->
  </div>

  {#if isLoadingMore}
    <div class="flex flex-col gap-6 pb-1.5 text-xs">
      {#each Array(1) as _, groupIndex}
        <div>
          <!-- <div class="mb-4 h-3.5 w-20 animate-pulse rounded-base bg-gray-100"></div> -->
          <ul>
            {#each Array(PLACEHOLDER_SEARCHES) as _, itemIndex}
              <li class="mb-4 flex animate-pulse items-center justify-between">
                <div class="h-3.5 w-[75%] rounded-base bg-gray-100 dark:bg-theme-background-tertiary"></div>
                <div class="mr-6 h-3.5 w-6 rounded-base bg-gray-100 dark:bg-theme-background-tertiary"></div>
              </li>
            {/each}
          </ul>
        </div>
      {/each}
    </div>
  {/if}
  <!-- {/if} -->
</div>

<style>
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: black transparent;
  }
  .custom-scrollbar::-webkit-scrollbar {
    width: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: black;
    border-radius: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background-color: transparent;
  }
</style>
